import { useEffect, useRef, useState } from 'react'
import { PRESET_RANGE_LABELS, PresetRange, TimeRange } from '../../../types/metrics'
import { AppButton } from '../../atoms/AppButton/AppButton'
import { AppDatePicker } from '../../atoms/AppDatePicker/AppDatePicker'

type Props = {
  timeRange: TimeRange
  customAfter: string
  customBefore: string
  triggerLabel: string
  onPresetChange: (range: PresetRange) => void
  onCustomApply: (after: string, before: string) => void
}

const TimeRangeDropdown = ({
  timeRange,
  customAfter,
  customBefore,
  triggerLabel,
  onPresetChange,
  onCustomApply,
}: Props): JSX.Element => {
  const [open, setOpen] = useState(false)
  const [pendingAfter, setPendingAfter] = useState(customAfter)
  const [pendingBefore, setPendingBefore] = useState(customBefore)
  const panelRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const firstPresetRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Element | null
      if (!panelRef.current || panelRef.current.contains(target)) return
      // PrimeReact's Calendar panel is portaled to document.body; treat clicks
      // landing inside it as inside the dropdown so date selection doesn't dismiss the panel.
      if (target?.closest?.('.p-datepicker')) return
      setOpen(false)
      triggerRef.current?.focus()
    }
    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [open])

  useEffect(() => {
    if (open) firstPresetRef.current?.focus()
  }, [open])

  const isPendingRangeValid = Boolean(pendingAfter && pendingBefore && pendingAfter <= pendingBefore)

  const handleToggle = (): void => {
    if (!open) {
      setPendingAfter(customAfter)
      setPendingBefore(customBefore)
    }
    setOpen(prev => !prev)
  }

  const handlePresetClick = (range: PresetRange): void => {
    onPresetChange(range)
    setOpen(false)
    triggerRef.current?.focus()
  }

  const handleApply = (): void => {
    if (!isPendingRangeValid) return
    onCustomApply(pendingAfter, pendingBefore)
    setOpen(false)
    triggerRef.current?.focus()
  }

  return (
    <div ref={panelRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Date range: ${triggerLabel}`}
        onClick={handleToggle}
        className="flex items-center gap-2 rounded-lg bg-orange px-6 py-3 text-2xl font-semibold text-white duration-200 hover:bg-orange-hover"
      >
        <span className="max-w-[16rem] truncate whitespace-nowrap" title={triggerLabel}>
          {triggerLabel}
        </span>
        <svg
          className={`h-3 w-3 shrink-0 duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={3}
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <dialog
          open
          aria-label="Date range"
          className="absolute right-0 top-full z-10 m-0 mt-2 flex w-80 flex-col gap-5 rounded-lg border border-soft-gray bg-white p-5 text-blue-gray shadow-[0_0_20px_5px_rgba(0,0,0,0.08)]"
        >
          <div className="flex flex-col gap-2">
            <span className="text-lg font-semibold uppercase tracking-wide text-stone-gray">Quick ranges</span>
            <ul className="flex flex-col">
              {(Object.keys(PRESET_RANGE_LABELS) as PresetRange[]).map((range, idx) => {
                const selected = timeRange === range
                return (
                  <li key={range}>
                    <button
                      ref={idx === 0 ? firstPresetRef : undefined}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => handlePresetClick(range)}
                      className={`flex w-full items-center justify-between rounded-md px-3 py-2.5 text-left text-2xl duration-200 hover:bg-just-gray ${
                        selected ? 'font-semibold text-dark-blue' : 'text-blue-gray'
                      }`}
                    >
                      <span>{PRESET_RANGE_LABELS[range]}</span>
                      {selected && (
                        <svg
                          className="h-4 w-4 text-orange"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={3}
                          aria-hidden="true"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
          <div className="border-t border-soft-gray" />
          <div className="flex flex-col gap-4">
            <span className="text-lg font-semibold uppercase tracking-wide text-stone-gray">Custom range</span>
            <div className="flex flex-col gap-2 text-2xl text-stone-gray">
              <AppDatePicker
                label="Start date"
                inputId="time-range-start-date"
                value={pendingAfter}
                onChange={setPendingAfter}
                appendTo={document.body}
              />
            </div>
            <div className="flex flex-col gap-2 text-2xl text-stone-gray">
              <AppDatePicker
                label="End date"
                inputId="time-range-end-date"
                value={pendingBefore}
                onChange={setPendingBefore}
                appendTo={document.body}
              />
            </div>
            <AppButton
              type="info"
              disabled={!isPendingRangeValid}
              onClick={handleApply}
              className="!h-12 !w-full !min-w-0 !px-6 !py-2 !text-2xl !rounded-lg"
            >
              Apply
            </AppButton>
          </div>
        </dialog>
      )}
    </div>
  )
}

export default TimeRangeDropdown
